import { createContext, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSnackbar } from 'notistack';
import type { SnackbarKey } from 'notistack';
import type { ReactNode, RefObject } from 'react';

import { EMPTY_ARRAY } from '@e-socna/empty';
import { getFragmentData } from '@e-socna/data-layer';
import { isDefined } from '@e-socna/utils';

import { DocumentEditorResolvers_QuickAccess_Mission_Fragment } from '../../components/DocumentEditor/resolvers/fragments/quick-access.fragment';

import { MissionContextStatus } from '../MissionContext/types/mission-context.type';
import { useMissionContext } from '../MissionContext/hooks/useMissionContext.hook';

import { SnackbarCloseButton } from './components/SnackbarCloseButton';
import { EMPTY_CONTEXT_DATA } from './constants/empty-context.constant';
import { GIS_UNAVAILABLE_ERROR } from './constants/warnings.constant';
import type {
  MissionWarning,
  MissionWarningsContextData,
} from './types/warning.type';

export const MissionWarningsContext =
  createContext<MissionWarningsContextData | null>(EMPTY_CONTEXT_DATA);

interface WarningStatus {
  readonly result: boolean;
  readonly snackbarKeyRef: RefObject<SnackbarKey | null>;
  readonly hasTriggeredRef: RefObject<boolean>;
  readonly warning: MissionWarning;
}

interface Props {
  readonly children: ReactNode;
  // The warnings are told by the context only, without notification.
  readonly isSilent?: boolean;
}

export const MissionWarningsContextProvider = ({
  children,
  isSilent = false,
}: Readonly<Props>) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const missionContext = useMissionContext();

  const gisWarningSnackbarKeyRef = useRef<SnackbarKey | null>(null);
  const hasTriggeredGisWarningRef = useRef(false);

  const isGisUnavailable = useMemo(() => {
    if (missionContext.status !== MissionContextStatus.Ready) {
      return false;
    }

    const { mission } = missionContext;
    const quickAccess = getFragmentData(
      DocumentEditorResolvers_QuickAccess_Mission_Fragment,
      mission,
    );
    const { distances } = quickAccess;

    return (
      !isDefined(distances.fastestFromQuotationEmitterInKm) ||
      !isDefined(distances.shortestFromQuotationEmitterInKm) ||
      !isDefined(distances.longestFromQuotationEmitterInKm)
    );
  }, [missionContext]);

  const warningStatusMap = useMemo(
    () => ({
      gis: {
        result: isGisUnavailable,
        snackbarKeyRef: gisWarningSnackbarKeyRef,
        hasTriggeredRef: hasTriggeredGisWarningRef,
        warning: GIS_UNAVAILABLE_ERROR,
      } satisfies WarningStatus,
    }),
    [isGisUnavailable],
  );

  const action = useCallback(
    (snackbarKey: SnackbarKey) => (
      <SnackbarCloseButton snackbarKey={snackbarKey} />
    ),
    [],
  );

  useEffect(() => {
    if (isSilent) {
      return;
    }

    Object.entries(warningStatusMap).forEach(([, status]) => {
      const { result, snackbarKeyRef, hasTriggeredRef, warning } = status;

      if (result && !hasTriggeredRef.current) {
        hasTriggeredRef.current = true;

        snackbarKeyRef.current = enqueueSnackbar(warning.message, {
          action,
          persist: true,
          preventDuplicate: true,
          style: { whiteSpace: 'pre-line' },
          variant: warning.severity,
        });
      }

      if (!result && hasTriggeredRef.current) {
        hasTriggeredRef.current = false;

        if (isDefined(snackbarKeyRef.current)) {
          closeSnackbar(snackbarKeyRef.current);
          snackbarKeyRef.current = null;
        }
      }
    });
  }, [warningStatusMap, enqueueSnackbar, closeSnackbar, action, isSilent]);

  useEffect(
    () => () => {
      Object.values(warningStatusMap).forEach(({ snackbarKeyRef }) => {
        if (isDefined(snackbarKeyRef.current)) {
          closeSnackbar(snackbarKeyRef.current);
        }
      });
    },
    [warningStatusMap, closeSnackbar],
  );

  const warnings = useMemo<readonly MissionWarning[]>(() => {
    // eslint-disable-next-line react-hooks/refs
    const activeWarnings = Object.values(warningStatusMap)
      .filter(({ result }) => result)
      .map(({ warning }) => warning);

    return activeWarnings.length ? activeWarnings : EMPTY_ARRAY;
  }, [warningStatusMap]);

  const contextValue = useMemo<MissionWarningsContextData>(
    () => ({
      warnings,
      isGisUnavailable,
    }),
    [warnings, isGisUnavailable],
  );

  return (
    <MissionWarningsContext.Provider value={contextValue}>
      {children}
    </MissionWarningsContext.Provider>
  );
};
